import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectColorTheme,
    selectData,
    selectEnabled,
    selectJobId,
    selectLegendRenameTimestamp,
    selectOrderedItemIndexes,
    selectPageSize,
    selectPDFLegendScale,
    selectPDFLegendSize,
    selectSecondaryStats,
    selectVisibleStatItems,
    setActiveMenu,
    setData,
} from "../PDFSlice"
import { formatArea } from "@nova/format"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { useTranslation } from "@nova/use-translation"
import { InsetComponent } from "../PDFLegend/Inset"
import {
    selectActiveFirePerimeterPolygons,
    selectVisibleFirePerimeters,
} from "features/entity-caching/fire-perimeters/selectors"
import { ExpandableQRCode } from "features/data-management/workspace/TODO-put-in-package/ExpandableQRCode"
import { PropsWithChildren, useEffect, useMemo } from "react"
import classNames from "classnames"
import { ScaleBar } from "../PDFLegend/PDFScaleBar"
import { LngLat } from "maplibre-gl"
import { config } from "@nova/configuration/browser"
import { useLegendItems } from "../PDFLegend/LegendItems/useLegendItems"
import { useDebounceDispatch } from "tools/store"
import * as Icons from "@nova/component-icons"
import { featureNameKey } from "../PDFPanel/buttons/PDFFeatures"
import { calculateArea } from "@nova/calculate-geospatial"
import { useViewport } from "features/map/GlobalViewState"
import { AreaUnit, DistanceUnit } from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"

const Feature = ({
    children,
    id,
    className,
}: PropsWithChildren & { id: string; className?: string }) => {
    const dispatch = useAppDispatch()
    return (
        <div
            className={classNames(
                "pointer-events-auto cursor-pointer hover:border-black border [&:not(:hover)]:border-[#00000000] p-0.5 rounded-md",
                className
            )}
            onClick={() => dispatch(setActiveMenu(id))}
        >
            {children}
        </div>
    )
}

type PDFAnnotationsType = {
    offsetX: number
    offsetY: number
    width: number
    height: number
    name: string
}

export const BORDER_THICKNESS = 12
export const BORDER_TICK_LENGTH = 0
export const DEFAULT_HEIGHT = 900

export const PDFLegend = ({ offsetX, offsetY, width, height, name }: PDFAnnotationsType) => {
    const pdfLegendSize = useAppSelector(selectPDFLegendSize())
    const pdfLegendScale = useAppSelector(selectPDFLegendScale())
    const innerWidth = width - (pdfLegendSize * height) / DEFAULT_HEIGHT
    const { t } = useTranslation()
    const { debounceDispatch } = useDebounceDispatch(100)
    const enabled = useAppSelector(selectEnabled())
    const text1 = useAppSelector(selectData("text1"))
    const projection = useAppSelector(selectData("projection"))
    const datum = useAppSelector(selectData("datum"))
    const createdAt = useAppSelector(selectData("createdAt"))
    const createdBy = useAppSelector(selectData("createdBy"))
    const operator = useAppSelector(selectData("operator"))
    const title = useAppSelector(selectData("title"))
    const subtitle = useAppSelector(selectData("subtitle"))
    const date = useAppSelector(selectData("date"))
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const polygons = useAppSelector(selectActiveFirePerimeterPolygons)
    const fire = useAppSelector(selectFireById(fireId))!
    const jobId = useAppSelector(selectJobId())
    const isDark = useAppSelector(selectColorTheme()) === "dark"
    const visibleStats = useAppSelector(selectVisibleStatItems())
    const visibleSecondaryStats = useAppSelector(selectSecondaryStats())

    const leftLogo = useAppSelector(selectData("leftLogo"))
    const rightLogo = useAppSelector(selectData("rightLogo"))
    const ratio = useAppSelector(selectData("ratio"))
    const { organizationSettingDistance } = config.use("organizationSettingDistance") as {
        organizationSettingDistance: DistanceUnit
    }
    const { organizationSettingArea } = config.use("organizationSettingArea") as {
        organizationSettingArea: AreaUnit
    }
    const legendRenameTimestamp = useAppSelector(selectLegendRenameTimestamp())
    const view = useViewport()

    const left = new LngLat(...(view.unproject([offsetX, offsetY]) as [number, number]))
    const right = new LngLat(...(view.unproject([offsetX, offsetY + height]) as [number, number]))
    const dist = left.distanceTo(right)
    const size = useAppSelector(selectPageSize())
    const calculatedRatio = dist / (parseFloat(size.split('"')[0]) * 0.0254)

    const { enabledLegendItems } = useLegendItems({
        offsetX,
        offsetY,
        width,
        height,
    })

    const displayedRatio =
        Math.abs(parseInt(ratio) / calculatedRatio - 1) < 0.01
            ? parseInt(ratio).toFixed(0)
            : calculatedRatio.toFixed(0)

    useEffect(() => {
        debounceDispatch(setData({ type: "displayedRatio", data: displayedRatio.toString() }))
    }, [displayedRatio, debounceDispatch])

    const perimeters = useAppSelector(selectVisibleFirePerimeters)

    const perimeterArea = useMemo(() => {
        return formatArea({
            value: perimeters.reduce((area, perimeter) => {
                return (
                    area +
                    perimeter.polygons.reduce(
                        (area, polygon) => area + calculateArea(polygon.coordinates),
                        0
                    )
                )
            }, 0),
            unit: organizationSettingArea,
        })
    }, [organizationSettingArea, perimeters])

    const orderedItemIndexes = useAppSelector(selectOrderedItemIndexes())
    const orderedItems = useMemo(
        () =>
            enabledLegendItems.sort((a, b) => {
                const indexA = orderedItemIndexes[a.id]
                const indexB = orderedItemIndexes[b.id]
                return indexA - indexB
            }),
        [orderedItemIndexes, enabledLegendItems]
    )

    const scale = (height / DEFAULT_HEIGHT) * pdfLegendScale

    useEffect(() => {
        if (enabled.features) {
            const featureContainer = document.getElementById("feature-container")
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.intersectionRatio < 1) {
                            entry.target.classList.add("invisible")
                        } else {
                            entry.target.classList.remove("invisible")
                        }
                    })
                },
                {
                    root: featureContainer,
                    threshold: 0,
                }
            )

            featureContainer?.childNodes.forEach((item) => observer.observe(item as Element))
            return () => {
                observer.disconnect()
            }
        }
    }, [enabled.features, orderedItems])

    return (
        <div
            className="absolute right-0 top-0 bg-white dark:bg-gray-900 dark:text-gray-0 border-l border-l-gray-[#DDDDDD] dark:border-l-gray-600 h-full"
            style={{ width: (pdfLegendSize * height) / DEFAULT_HEIGHT }}
        >
            <div
                className={classNames(
                    "absolute right-0 top-0 flex flex-col gap-[6px] p-3 h-full overflow-hidden"
                )}
                style={{
                    transformOrigin: "top right",
                    transform: `scale(${scale})`,
                    height: height / scale,
                    width: (pdfLegendSize * height) / DEFAULT_HEIGHT / scale,
                }}
            >
                {enabled.title && (
                    <Feature id={"title"}>
                        <div className="flex flex-col text-center">
                            <div className="font-semibold text-[14px] break-words">
                                {t(title || fire.name)}
                            </div>
                            <div className="font-normal text-[10px] break-words">{t(subtitle)}</div>
                            <div className="font-normal text-[10px]">{t(date)}</div>
                            {perimeters.length > 0 && (
                                <div className="font-normal text-[10px]">{perimeterArea}</div>
                            )}
                        </div>
                    </Feature>
                )}
                {enabled.features && (
                    <Feature
                        id="features"
                        key={legendRenameTimestamp}
                        className="flex-shrink min-h-0 overflow-hidden flex"
                    >
                        <div className="flex flex-col" id="feature-container">
                            {orderedItems.map((item) => {
                                const localRename = JSON.parse(
                                    localStorage.getItem(featureNameKey) || "{}"
                                )[item.id] as string
                                const isStatVisible = visibleStats[item.id]
                                const isSecondaryStatVisible = visibleSecondaryStats[item.id]
                                return (
                                    <div className="p-0.5" key={item.id}>
                                        <div className="flex flex-row w-full ">
                                            <div className="mr-1.5 flex w-[14px] h-[14px] my-auto items-center">
                                                {item.icon}
                                            </div>
                                            <div className="flex flex-col overflow-hidden w-full">
                                                <div className="text-[9px] leading-[15px] overflow-hidden whitespace-nowrap text-ellipsis w-full">
                                                    {t(localRename || item.title)}
                                                </div>
                                                {item.stat &&
                                                    (isStatVisible || isSecondaryStatVisible) && (
                                                        <div className="text-[9px] leading-[15px] overflow-hidden whitespace-nowrap text-ellipsis w-full">
                                                            {isStatVisible && item.stat.stat}
                                                            {isStatVisible &&
                                                                isSecondaryStatVisible &&
                                                                " | "}
                                                            {isSecondaryStatVisible &&
                                                                item.secondaryStat?.stat}
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </Feature>
                )}
                {enabled.inset && (
                    <div>
                        <InsetComponent
                            offsetX={offsetX}
                            offsetY={offsetY}
                            width={innerWidth}
                            height={height}
                            polygons={polygons}
                        />
                    </div>
                )}
                <div className="flex grow" />
                {(enabled.scalebar || enabled.ratio) && (
                    <div className="mt-1">
                        {enabled.scalebar && (
                            <ScaleBar
                                view={view}
                                u={organizationSettingDistance}
                                height={height}
                                color={isDark ? "#FFF" : "#000"}
                            />
                        )}
                        {enabled.ratio && (
                            <Feature id="ratio">
                                <div className="text-center text-[10px]">
                                    1:
                                    {t(displayedRatio)}
                                </div>
                            </Feature>
                        )}
                    </div>
                )}
                {(enabled.text1 || enabled.text2) && (
                    <>
                        {enabled.text1 && (
                            <Feature id="text1">
                                <div className="overflow-hidden break-words text-[7.63px] whitespace-pre-wrap dark:text-gray-200">
                                    {text1}
                                </div>
                            </Feature>
                        )}
                        {enabled.text2 && (
                            <Feature id="text2">
                                <div className="overflow-hidden break-words text-[7.63px] whitespace-pre-wrap dark:text-gray-200">
                                    {enabled.projection && <div>{`Projection: ${projection}`}</div>}
                                    {enabled.datum && <div>{`Datum: ${datum}`}</div>}
                                    {enabled.createdAt && <div>{`Date Created: ${createdAt}`}</div>}
                                    {enabled.createdBy && (
                                        <div>{`Map Produced by: ${createdBy}`}</div>
                                    )}
                                    {enabled.operator && (
                                        <div>{`Camera Operator / Type: ${operator}`}</div>
                                    )}
                                </div>
                            </Feature>
                        )}
                    </>
                )}
                {enabled.logos && (leftLogo || rightLogo) && (
                    <Feature id="logos">
                        <div className="flex flex-row justify-around gap-1 max-h-[75px]">
                            {leftLogo && (
                                <div className="flex-1 flex">
                                    <img
                                        src={leftLogo}
                                        alt="left logo"
                                        className="m-auto max-h-full"
                                    />
                                </div>
                            )}
                            {rightLogo && (
                                <div className="flex-1 flex">
                                    <img
                                        src={rightLogo}
                                        alt="right logo"
                                        className="m-auto max-h-full"
                                    />
                                </div>
                            )}
                        </div>
                    </Feature>
                )}
                <div className="flex pb-2">
                    {enabled.compass && (
                        <div className="flex flex-1">
                            <div
                                className={classNames(
                                    "w-9 h-9 flex items-center justify-center rounded-full cursor-pointer pointer-events-auto border m-auto",
                                    {
                                        "bg-black-600 hover:bg-gray-700 border-white-300 hover:border-white-400":
                                            isDark,
                                        "bg-white-500 hover:bg-gray-0 border-black-300 hover:border-black-400":
                                            !isDark,
                                    }
                                )}
                            >
                                <Icons.SmallIcon
                                    Icon={
                                        !isDark ? Icons.CompassNeedleDark : Icons.CompassNeedleLight
                                    }
                                    style={{ transform: `rotate(${-view.bearing}deg)` }}
                                    className={classNames({
                                        "text-gray-500": isDark,
                                        "text-gray-0": !isDark,
                                    })}
                                />
                            </div>
                        </div>
                    )}

                    {enabled.qrcode && (
                        <div className="flex flex-1">
                            <div className="w-max m-auto">
                                <ExpandableQRCode
                                    title={t("Map PDF")}
                                    size={46}
                                    value={`${window.location.origin}/api/organization/${workspaceId}/job/${jobId}/map.pdf?n=${name}`}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="text-[7.63px] text-center">{t("Map created with mapnova.com")}</div>
            </div>
        </div>
    )
}
